/**
 * Small decorative marks, drawn rather than typed.
 *
 * A trend arrow or a tick written as `▲` or `✓` depends on whatever font the
 * renderer ends up using carrying that glyph. The standard PDF faces do not
 * carry either, so a report that looked right in the browser exported with a
 * question mark in place of the arrow. Drawing them as paths removes the
 * dependency entirely, and they stay crisp at any size.
 *
 * Colours are explicit rather than `currentColor`: not every renderer resolves
 * it inside SVG, and these marks are always given a deliberate colour anyway.
 */
import { h, type VNode } from "@broadpaper/core";

function svg(size: number, children: VNode[]): VNode {
  return h(
    "svg",
    { width: size, height: size, viewBox: "0 0 12 12", xmlns: "http://www.w3.org/2000/svg", "aria-hidden": "true", style: { display: "block", "flex-shrink": "0" } },
    children
  );
}

/** A solid triangle, for trend indicators. */
export function triangleMark(direction: "up" | "down", size: number, color: string): VNode {
  const d = direction === "up" ? "M6 2 L11 9.5 L1 9.5 Z" : "M6 10 L1 2.5 L11 2.5 Z";
  return svg(size, [h("path", { d, fill: color })]);
}

/** A tick, for success states. */
export function checkMark(size: number, color: string): VNode {
  return svg(size, [h("path", { d: "M2 6.4 L4.8 9.2 L10 3.2", fill: "none", stroke: color, "stroke-width": 1.8, "stroke-linecap": "round", "stroke-linejoin": "round" })]);
}

/** A filled dot, for neutral states. */
export function dotMark(size: number, color: string): VNode {
  return svg(size, [h("circle", { cx: 6, cy: 6, r: 2.6, fill: color })]);
}
